import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
    FiArrowLeft, FiTrash2, FiSearch, FiX, FiArrowUp, FiArrowDown, FiPrinter, FiCircle,
    FiChevronsLeft, FiChevronLeft, FiChevronRight, FiChevronsRight, FiSettings
} from 'react-icons/fi';
import {
    flexRender,
    getCoreRowModel,
    useReactTable,
    getSortedRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
} from '@tanstack/react-table';
import Swal from 'sweetalert2';
import axios from 'axios';
import { API_BASE_URL } from '../../../config';
import styles from './ViewVeiculos.module.css';
import generateFleetReport from './generateFleetReport';
import Portal from '../../../components/Portal/Portal'; 
import PageHeader from '../../../components/PageHeader/PageHeader';
import LoadingOverlay from '../../../components/LoadingOverlay/LoadingOverlay'; // Importado

const StatusBadge = ({ status }) => {
    const statusInfo = useMemo(() => {
        const lowerCaseStatus = status ? status.toLowerCase() : '';
        switch (lowerCaseStatus) {
            case 'disponivel':
                return { text: 'Disponível', className: styles.statusDisponivel };
            case 'in-progress':
                return { text: 'Em Manutenção', className: styles.statusEmManutencao };
            case 'to-do':
                return { text: 'Agendada', className: styles.statusAFazer };
            case 'done':
            case 'concluido': 
                return { text: 'Concluído', className: styles.statusConcluido };
            default:
                return { text: status || 'Não definido', className: styles.statusDefault };
        }
    }, [status]);

    return (
        <span className={`${styles.statusBadge} ${statusInfo.className}`}>
            {statusInfo.text}
        </span>
    );
};

const StatusLegend = () => (
    <div className={styles.legendContainer}>
        <div className={styles.legendItem}><FiCircle className={styles.legendIconDisponivel} /> Disponível</div>
        <div className={styles.legendItem}><FiCircle className={styles.legendIconEmManutencao} /> Em Manutenção</div>
        <div className={styles.legendItem}><FiCircle className={styles.legendIconAFazer} /> Agendada</div>
        <div className={styles.legendItem}><FiCircle className={styles.legendIconConcluido} /> Concluído</div>
    </div>
);

const ViewVeiculos = () => {
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [isLoading, setIsLoading] = useState(true); // Mantido para feedback interno
    const [pageLoading, setPageLoading] = useState(true); // Novo estado para o overlay
    const [error, setError] = useState(null);
    const [globalFilter, setGlobalFilter] = useState('');
    const [columnVisibility, setColumnVisibility] = useState({});
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    const [settingsPosition, setSettingsPosition] = useState({ top: 0, left: 0 });

    const [isReportModalOpen, setIsReportModalOpen] = useState(false);
    const [reportColumns, setReportColumns] = useState({
        placa: true,
        marca: true,
        modelo: true,
        anoModelo: true,
        tipoVeiculo: true,
        quilometragem: true,
        status: true,
    });
    
    const settingsButtonRef = useRef(null);
    const settingsDropdownRef = useRef(null);

    useEffect(() => {
        const savedVisibility = localStorage.getItem('vehicleColumnVisibility');
        if (savedVisibility) {
            setColumnVisibility(JSON.parse(savedVisibility));
        }
    }, []);

    useEffect(() => {
        if (Object.keys(columnVisibility).length > 0) {
            localStorage.setItem('vehicleColumnVisibility', JSON.stringify(columnVisibility));
        }
    }, [columnVisibility]);
    
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isSettingsOpen && settingsDropdownRef.current && !settingsDropdownRef.current.contains(event.target) && !settingsButtonRef.current.contains(event.target)) {
                setIsSettingsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isSettingsOpen]);

    const fetchVehicles = useCallback(async () => {
        setIsLoading(true); // Mantido para lógica interna se necessário
        setError(null);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_BASE_URL}/api/veiculos`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setData(response.data);
        } catch (err) {
            console.error("Erro ao buscar veículos:", err);
            setError("Não foi possível carregar a frota de veículos.");
            Swal.fire('Erro!', 'Não foi possível carregar os dados da frota.', 'error');
        } finally {
            setIsLoading(false); // Mantido
        }
    }, []);

    useEffect(() => {
        const MIN_LOADING_TIME = 300;
        const startTime = Date.now();
        setPageLoading(true);

        const loadData = async () => {
            await fetchVehicles(); // Chama a função que busca os dados

            const endTime = Date.now();
            const duration = endTime - startTime;
            const remainingTime = MIN_LOADING_TIME - duration;

            if (remainingTime > 0) {
                setTimeout(() => setPageLoading(false), remainingTime);
            } else {
                setPageLoading(false);
            }
        };

        loadData();
    }, [fetchVehicles]); // Depende de fetchVehicles

    const formatMedidor = useCallback((valor, unidade = 'km') => {
    if (isNaN(valor) || valor === null) return `0 ${unidade}`;
    return parseFloat(valor).toLocaleString('pt-BR') + ` ${unidade}`;
}, []);

    const handleDelete = useCallback((id) => {
        Swal.fire({
            title: 'Tem certeza?',
            text: "Esta ação não poderá ser revertida!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Sim, excluir!',
            cancelButtonText: 'Cancelar'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const token = localStorage.getItem('token');
                    await axios.delete(`${API_BASE_URL}/api/veiculos/${id}`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                    });
                    Swal.fire('Excluído!', 'O veículo foi removido com sucesso.', 'success');
                    // Refetch data without showing full page loading again
                    const startTime = Date.now();
                    await fetchVehicles(); 
                    // No need for minimum time here, just refresh data
                    
                } catch (err) {
                    console.error("Erro ao excluir veículo:", err);
                    Swal.fire('Erro!', err.response?.data?.message || 'Não foi possível remover o veículo.', 'error');
                }
            }
        });
    }, [fetchVehicles]);

    const handleRowClick = useCallback((id) => {
        navigate(`/frota/veiculos/${id}`);
    }, [navigate]);

    const handleToggleSettings = () => {
        if (settingsButtonRef.current) {
            const rect = settingsButtonRef.current.getBoundingClientRect();
            setSettingsPosition({
                top: rect.bottom + window.scrollY + 8,
                left: rect.left + window.scrollX,
            });
        }
        setIsSettingsOpen(prev => !prev);
    };

    const columns = useMemo(() => [
        { accessorKey: 'tipoVeiculo', header: 'Tipo' },
        { accessorKey: 'marca', header: 'Marca' },
        { accessorKey: 'modelo', header: 'Modelo' },
        { accessorKey: 'placa', header: 'Placa' },
        {
            accessorKey: 'status_manutencao',
            header: 'Status',
            cell: info => <StatusBadge status={info.getValue()} />,
            enableHiding: false,
        },
        { accessorKey: 'anoModelo', header: 'Ano' },
        {
    id: 'quilometragem',
    header: 'KM/Horas',
    accessorFn: row => row.tipoVeiculo === 'MAQUINA' ? row.horimetro : row.quilometragem,
    cell: info => {
        const row = info.row.original;
        const isMaquina = row.tipoVeiculo === 'MAQUINA';
        return formatMedidor(isMaquina ? row.horimetro : row.quilometragem, isMaquina ? 'h' : 'km');
    },
},
        {
            id: 'actions', header: 'Ações', cell: ({ row }) => (
                <div className={styles.tableActions}>
                    <button className={`${styles.actionBtn} ${styles.deleteBtn}`} onClick={(e) => { e.stopPropagation(); handleDelete(row.original.id); }} title="Excluir">
                        <FiTrash2 />
                    </button>
                </div>
            ),
            enableSorting: false,
            enableHiding: false,
        },
    ], [handleDelete, formatMedidor]);

    const table = useReactTable({
        data,
        columns,
        state: { 
            globalFilter,
            columnVisibility,
        },
        onGlobalFilterChange: setGlobalFilter,
        onColumnVisibilityChange: setColumnVisibility,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        initialState: {
            pagination: {
                pageSize: 10,
            },
        },
    });

    const handleOpenReportModal = () => {
        const currentRows = table.getFilteredRowModel().rows;
        if (currentRows.length === 0) {
            Swal.fire('Atenção', 'Não há veículos (com base nos filtros atuais) para incluir no relatório.', 'info');
            return;
        }
        setIsReportModalOpen(true);
    };

    const handleReportColumnChange = (event) => {
        const { name, checked } = event.target;
        setReportColumns(prev => ({ ...prev, [name]: checked }));
    };

    const handleConfirmAndGenerateReport = () => {
        const currentRows = table.getFilteredRowModel().rows.map(row => row.original);
        const selectedCols = Object.values(reportColumns).some(v => v); 

        if (!selectedCols) {
            Swal.fire('Atenção', 'Selecione pelo menos uma coluna para gerar o relatório.', 'warning');
            return;
        }
        
        generateFleetReport(currentRows, reportColumns);
        setIsReportModalOpen(false);
    };
    
    return (
        <div className={styles.container}>
            {pageLoading && <LoadingOverlay text="Carregando frota..." />}

            {!pageLoading && (
                <>
                    <PageHeader 
                        title="Frota de Veículos"
                        subtitle="Gerencie, pesquise e visualize todos os veículos cadastrados."
                        backTo="/sistema-frota"
                    />

                    <div className={styles.controlsCard}>
                        <div className={styles.controls}>
                            <div className={styles.controlsLeft}>
                                <button className={styles.reportBtn} onClick={handleOpenReportModal}>
                                    <FiPrinter />
                                    Gerar Relatório
                                </button>
                                <div className={styles.settingsWrapper}>
                                    <button ref={settingsButtonRef} className={styles.settingsBtn} onClick={handleToggleSettings}>
                                        <FiSettings />
                                        Colunas
                                    </button>
                                </div>
                            </div>

                            <div className={styles.searchInputWrapper}>
                                <FiSearch className={styles.searchIcon} />
                                <input
                                    type="text"
                                    placeholder="Pesquisar em toda a tabela..."
                                    value={globalFilter}
                                    onChange={e => setGlobalFilter(e.target.value)}
                                    className={styles.searchInput}
                                />
                                {globalFilter && (
                                    <button className={styles.clearSearchBtn} onClick={() => setGlobalFilter('')}>
                                        <FiX />
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                    
                    <StatusLegend />

                    <div className={styles.tableCard}>
                        <div className={styles.desktopTableView}>
                            <table className={styles.table}>
                                <thead>
                                    {table.getHeaderGroups().map(headerGroup => (
                                        <tr key={headerGroup.id}>
                                            {headerGroup.headers.map(header => (
                                                <th key={header.id} className={header.column.getCanSort() ? styles.sortable : ''} onClick={header.column.getToggleSortingHandler()}>
                                                    <div className={styles.tableHeaderContent}>
                                                        {flexRender(header.column.columnDef.header, header.getContext())}
                                                        {header.column.getCanSort() && (
                                                            <span className={`${styles.sortIcon} ${!header.column.getIsSorted() && styles.sortIconNeutral}`}>
                                                                {header.column.getIsSorted() === 'asc' ? <FiArrowUp /> : <FiArrowDown />}
                                                            </span>
                                                        )}
                                                    </div>
                                                </th>
                                            ))}
                                        </tr>
                                    ))}
                                </thead>
                                <tbody>
                                    {table.getRowModel().rows.length > 0 ? (
                                        table.getRowModel().rows.map(row => (
                                            <tr key={row.id} className={styles.tableRowClickable} onClick={() => handleRowClick(row.original.id)}>
                                                {row.getVisibleCells().map(cell => (
                                                    <td key={cell.id}>
                                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={columns.length} className={styles.noDataMessage}>
                                                {error ? error : "Nenhum veículo encontrado."}
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        <div className={styles.mobileCardView}>
                            {table.getRowModel().rows.length > 0 ? (
                                table.getRowModel().rows.map(row => (
                                    <div key={row.id} className={styles.vehicleCard} onClick={() => handleRowClick(row.original.id)}>
                                        <div className={styles.cardHeader}>
                                            <h3 className={styles.cardTitle}>{row.original.marca} {row.original.modelo}</h3>
                                            <StatusBadge status={row.original.status_manutencao} />
                                        </div>
                                        <div className={styles.cardBody}>
                                            <div><strong>Placa:</strong> {row.original.placa || 'N/A'}</div>
                                            <div><strong>Ano:</strong> {row.original.anoModelo}</div>
                                            <div><strong>Tipo:</strong> {row.original.tipoVeiculo}</div>
                                            <div>
    <strong>{row.original.tipoVeiculo === 'MAQUINA' ? 'Horímetro:' : 'KM:'}</strong>{' '}
    {formatMedidor(
        row.original.tipoVeiculo === 'MAQUINA' ? row.original.horimetro : row.original.quilometragem,
        row.original.tipoVeiculo === 'MAQUINA' ? 'h' : 'km'
    )}
</div>
                                        </div>
                                        <div className={styles.cardFooter}>
                                            <button className={`${styles.actionBtn} ${styles.deleteBtn}`} onClick={(e) => { e.stopPropagation(); handleDelete(row.original.id); }}><FiTrash2 /></button>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <p className={styles.noDataMessage}>{error ? error : "Nenhum veículo encontrado."}</p>
                            )}
                        </div>

                        {table.getPageCount() > 1 && (
                            <div className={styles.paginationControls}>
                                <button 
                                    className={styles.paginationBtn} 
                                    onClick={() => table.setPageIndex(0)} 
                                    disabled={!table.getCanPreviousPage()}
                                    title="Primeira página"
                                >
                                    <FiChevronsLeft />
                                </button>
                                <button 
                                    className={styles.paginationBtn} 
                                    onClick={() => table.previousPage()} 
                                    disabled={!table.getCanPreviousPage()}
                                    title="Página anterior"
                                >
                                    <FiChevronLeft />
                                </button>
                                
                                <span className={styles.pageInfo}>
                                    Página{' '}
                                    <strong>
                                        {table.getState().pagination.pageIndex + 1} de {table.getPageCount()}
                                    </strong>
                                </span>

                                <button 
                                    className={styles.paginationBtn} 
                                    onClick={() => table.nextPage()} 
                                    disabled={!table.getCanNextPage()}
                                    title="Próxima página"
                                >
                                    <FiChevronRight />
                                </button>
                                <button 
                                    className={styles.paginationBtn} 
                                    onClick={() => table.setPageIndex(table.getPageCount() - 1)} 
                                    disabled={!table.getCanNextPage()}
                                    title="Última página"
                                >
                                    <FiChevronsRight />
                                </button>
                            </div>
                        )}
                    </div>

                    {isSettingsOpen && (
                        <Portal>
                            <div ref={settingsDropdownRef} className={styles.settingsDropdown} style={{ top: `${settingsPosition.top}px`, left: `${settingsPosition.left}px` }}>
                                <div className={styles.dropdownHeader}>Exibir/Ocultar Colunas</div>
                                {table.getAllLeafColumns().map(column => {
                                    if (column.getCanHide()) {
                                        return (
                                            <label key={column.id} className={styles.dropdownItem}>
                                                <input
                                                    type="checkbox"
                                                    checked={column.getIsVisible()}
                                                    onChange={column.getToggleVisibilityHandler()}
                                                />
                                                {typeof column.columnDef.header === 'string' ? column.columnDef.header : column.id}
                                            </label>
                                        );
                                    }
                                    return null;
                                })}
                            </div>
                        </Portal>
                    )}

                    {isReportModalOpen && (
                        <Portal>
                            <div className={styles.modalOverlay}>
                                <div className={styles.modalContent}>
                                    <div className={styles.modalHeader}>
                                        <h2>Selecionar Colunas para o Relatório</h2>
                                        <button onClick={() => setIsReportModalOpen(false)} className={styles.modalCloseButton}><FiX /></button>
                                    </div>
                                    <div className={styles.modalBody}>
                                        <p>Marque as colunas que você deseja incluir no relatório em PDF.</p>
                                        <div className={styles.reportColumnSelector}>
                                            {Object.keys(reportColumns).map(colName => (
                                                <label key={colName} className={styles.dropdownItem}>
                                                    <input
                                                        type="checkbox"
                                                        name={colName}
                                                        checked={reportColumns[colName]}
                                                        onChange={handleReportColumnChange}
                                                    />
                                                    {/* Usando getHeader para nome amigável se disponível */}
                                                    {columns.find(c => c.accessorKey === colName)?.header || colName.charAt(0).toUpperCase() + colName.slice(1).replace(/([A-Z])/g, ' $1')}
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                    <div className={styles.modalFooter}>
                                        <button onClick={() => setIsReportModalOpen(false)} className={`${styles.actionButton} ${styles.cancelButton}`}>Cancelar</button>
                                        <button onClick={handleConfirmAndGenerateReport} className={`${styles.actionButton} ${styles.confirmButton}`}>Confirmar e Gerar</button>
                                    </div>
                                </div>
                            </div>
                        </Portal>
                    )}
                </>
            )}
        </div>
    );
};

export default ViewVeiculos;